import { faFilter } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Checkbox, Input, InvertButton, PrimaryButton } from 'component-library';
import { useMemo, useState } from 'react';
import { useDetectClickOutside } from 'react-detect-click-outside';
import { ListFilterOption } from '../../helpers/UseListViewFilters';

export type ListViewFilterPanelColumn<K extends string> = {
  key: K;
  label: string;
  options: ListFilterOption[];
  searchable?: boolean;
  searchPlaceholder?: string;
  scrollClassName?: string;
};

interface Props<K extends string> {
  title: string;
  columns: ListViewFilterPanelColumn<K>[];
  selectedByColumn: Record<K, string[]>;
  searchByColumn: Partial<Record<K, string>>;
  activeCount: number;
  isMobile: boolean;
  triggerClassName?: string;
  onToggleValue: (key: K, value: string) => void;
  onSearchChange: (key: K, value: string) => void;
  onClearAll: () => void;
}

function ListViewFilterPanel<K extends string>({
  title,
  columns,
  selectedByColumn,
  searchByColumn,
  activeCount,
  isMobile,
  triggerClassName,
  onToggleValue,
  onSearchChange,
  onClearAll,
}: Props<K>) {
  const [isOpen, setIsOpen] = useState(false);
  const filterPopupRef = useDetectClickOutside({
    onTriggered: () => setIsOpen(false),
  });

  const hasColumnFilters = activeCount > 0;
  const popupClassName = useMemo(
    () =>
      `z-[2000] bg-white border border-gray-10 shadow-dropdown ${isMobile
        ? 'fixed left-2 right-6 top-6 max-h-[calc(100vh-6rem)]'
        : 'fixed top-6 right-6 w-[620px] xl:w-[760px] max-w-[92vw] max-h-[80vh]'
      }`,
    [isMobile]
  );
  const gridClassName = useMemo(
    () =>
      `grid gap-4 p-4 overflow-y-auto ${isMobile ? 'grid-cols-1 max-h-[calc(100vh-10rem)]' : 'md:grid-cols-2 max-h-[75vh]'
      }`,
    [isMobile]
  );

  return (
    <div className='relative' ref={filterPopupRef}>
      <button
        type='button'
        className={
          triggerClassName ||
          'h-8 px-3 bg-gray-3 border border-gray-10 text-gray-80 prose-paragraphSmaller flex items-center gap-2'
        }
        onClick={() => setIsOpen((prev) => !prev)}
      >
        <FontAwesomeIcon icon={faFilter} className='text-gray-60' />
        <span>Filter</span>
        {activeCount > 0 && (
          <span className='bg-primary-100 text-white rounded-full min-w-[18px] h-[18px] px-1 text-[11px] leading-[18px] text-center'>
            {activeCount}
          </span>
        )}
      </button>
      {isOpen && (
        <div className={popupClassName}>
          <div className='flex justify-between items-center px-3 py-2 border-b border-gray-10'>
            <span className='prose-labelTableColumn text-gray-80'>{title}</span>
            <div className='flex items-center gap-3'>
              {hasColumnFilters && (
                <InvertButton size='tiny' label='Clear all' onClick={onClearAll} />
              )}
              <PrimaryButton size='tiny' label='Close' onClick={() => setIsOpen(false)} />
            </div>
          </div>
          <div className={gridClassName}>
            {columns.map((column) => {
              const selectedValues = selectedByColumn[column.key];
              const optionSearchValue = searchByColumn[column.key] || '';
              const visibleOptions = optionSearchValue
                ? column.options.filter((option) =>
                  option.label.toLowerCase().includes(optionSearchValue.toLowerCase().trim())
                )
                : column.options;

              return (
                <div key={column.key}>
                  <div className='prose-paragraphSmaller text-gray-60 mb-1'>{column.label}</div>
                  {column.searchable && (
                    <div className='mb-2'>
                      <Input
                        placeholder={column.searchPlaceholder || `Search ${column.label.toLowerCase()}...`}
                        value={optionSearchValue}
                        onChange={(value: string) => onSearchChange(column.key, value)}
                      />
                    </div>
                  )}
                  <div
                    className={`border border-gray-10 bg-gray-3 p-2 overflow-y-auto ${column.scrollClassName || 'max-h-52'
                      }`}
                  >
                    {visibleOptions.length > 0 ? (
                      visibleOptions.map((option: ListFilterOption) => (
                        <div
                          key={option.value}
                          className='flex items-center gap-2 py-1 cursor-pointer'
                          onClick={() => onToggleValue(column.key, option.value)}
                        >
                          <Checkbox active={selectedValues.includes(option.value)} onClick={() => undefined} />
                          <span className='prose-paragraphSmaller text-gray-80 truncate'>{option.label}</span>
                        </div>
                      ))
                    ) : (
                      <span className='prose-paragraphSmaller text-gray-40 italic'>No matching values</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

export default ListViewFilterPanel;
